'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AlertCost,
  type AlertQuote,
  type AlertSubscription,
  type AlertSubscriptionsResponse,
  insufficientCreditsSchema,
} from '@/lib/alerts-schemas';
import { useSession } from '@/hooks/use-session';

export const ALERTS_KEY = ['alerts'] as const;
const SUBSCRIPTIONS_KEY = ['alerts', 'subscriptions'] as const;
const COST_KEY = ['alerts', 'cost'] as const;
const QUOTE_KEY = ['alerts', 'quote'] as const;

/**
 * An alert could not be prepaid: the backend answered 402
 * `{error:'insufficient_credits', needed, balance}`. A product state, not a
 * failure — callers show the top-up prompt.
 */
export class InsufficientCreditsError extends Error {
  constructor(
    readonly needed: number | null,
    readonly balance: number | null,
  ) {
    super('Insufficient credits for this alert');
    this.name = 'InsufficientCreditsError';
  }
}

export class AlertsRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Alerts request failed with ${status}`);
    this.name = 'AlertsRequestError';
  }
}

/**
 * Throws `InsufficientCreditsError` for a 402 from any alert-creating route
 * (feeds, company and list watches, the star button). Exported so the
 * watch hooks share the one parser.
 */
export async function throwIfInsufficientCredits(res: Response): Promise<void> {
  if (res.status !== 402) return;
  const parsed = insufficientCreditsSchema.safeParse(
    await res.clone().json().catch(() => null),
  );
  throw new InsufficientCreditsError(
    parsed.success ? (parsed.data.needed ?? null) : null,
    parsed.success ? (parsed.data.balance ?? null) : null,
  );
}

async function alertsRequest<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: init?.body
      ? { 'Content-Type': 'application/json', ...(init.headers ?? {}) }
      : init?.headers,
  });
  await throwIfInsufficientCredits(res);
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) throw new AlertsRequestError(res.status, body);
  return (body as { data: T } | null)?.data as T;
}

/** Every alert of the signed-in user — companies, lists and feeds. */
export function useAlertSubscriptions() {
  const { data: session } = useSession();
  const query = useQuery<AlertSubscriptionsResponse>({
    queryKey: SUBSCRIPTIONS_KEY,
    queryFn: () => alertsRequest('/api/alerts/subscriptions'),
    enabled: !!session,
    staleTime: 30 * 1000,
  });
  return {
    items: query.data?.items ?? [],
    totalDailyCost: query.data?.total_daily_cost ?? null,
    balance: query.data?.balance ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** Daily alert cost, balance and days left. */
export function useAlertCost(enabled = true) {
  const { data: session } = useSession();
  return useQuery<AlertCost>({
    queryKey: COST_KEY,
    queryFn: () => alertsRequest('/api/alerts/cost'),
    enabled: !!session && enabled,
    staleTime: 30 * 1000,
  });
}

export interface AlertQuoteInput {
  subject_type: 'company' | 'list' | 'feed';
  eu_id?: string;
  list_id?: number;
  feed_type?: string;
  cadence: string;
  criteria?: string[];
  filters?: Record<string, unknown>;
  saved_search_id?: number;
  /** Quote an edit of this subscription (a feed without it is always new). */
  public_id?: string;
}

/**
 * The price of a proposed alert. `null` input disables the query (e.g. until
 * the dialog reaches the step that shows it).
 */
export function useAlertQuote(input: AlertQuoteInput | null) {
  const { data: session } = useSession();
  return useQuery<AlertQuote>({
    queryKey: [...QUOTE_KEY, input],
    queryFn: () =>
      alertsRequest('/api/alerts/quote', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    enabled: !!session && input !== null,
    staleTime: 15 * 1000,
    retry: false,
  });
}

export interface FeedAlertInput {
  feed_type: string;
  filters: Record<string, unknown>;
  cadence: string;
  channel: string;
  name?: string;
  recipient_emails?: string[];
  saved_search_id?: number;
}

export interface AlertPatch {
  name?: string;
  filters?: Record<string, unknown>;
  cadence?: string;
  channel?: string;
  recipient_emails?: string[];
  criteria?: string[];
  paused?: boolean;
}

/**
 * Create / edit / pause / delete. Every mutation refreshes the alert queries
 * and the watch views (a company or list alert edited here is the same row
 * the watchlist and list pages show).
 */
export function useAlertMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ALERTS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['watch'] });
    void queryClient.invalidateQueries({ queryKey: ['watchlist'] });
    void queryClient.invalidateQueries({ queryKey: ['lists'] });
  };

  const create = useMutation({
    mutationFn: (input: FeedAlertInput) =>
      alertsRequest<AlertSubscription>('/api/alerts/subscriptions', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSettled: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ publicId, patch }: { publicId: string; patch: AlertPatch }) =>
      alertsRequest<AlertSubscription>(
        `/api/alerts/subscriptions/${encodeURIComponent(publicId)}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      ),
    onSettled: invalidate,
  });
  const remove = useMutation({
    mutationFn: (publicId: string) =>
      alertsRequest<unknown>(
        `/api/alerts/subscriptions/${encodeURIComponent(publicId)}`,
        { method: 'DELETE' },
      ),
    onSettled: invalidate,
  });

  return { create, update, remove };
}
